import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useMemo, useState, useEffect } from 'react';
import { css, useTheme, jsx } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { math } from 'polished';
import { textStyles } from '@paypalcorp/pp-react-text';
import { devWarning, utilStyles } from '@paypalcorp/pp-react-utils';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { ChevronDownIcon } from '@paypalcorp/pp-react-icons';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysSpaceXs = tokens.sysSpaceXs,
      sysSpaceLg = tokens.sysSpaceLg,
      sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
      sysColorBackgroundHighContrast = tokens.sysColorBackgroundHighContrast,
      sysMotionDurationMoveSmall = tokens.sysMotionDurationMoveSmall;

  var _utilStyles = utilStyles(tokens),
      focusStyles = _utilStyles.focusStyles,
      variables = _utilStyles.variables;

  var _textStyles = textStyles(tokens),
      textBody = _textStyles.body,
      headingSm = _textStyles.headingSm,
      title = _textStyles.title;

  var ppuiEaseMoveSmall = variables.ppuiEaseMoveSmall;
  var headerPaddingRight = math("".concat(sysSpaceLg, " * 3.25"));
  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: accordion_base;\n    width: 100%;\n    border-collapse: collapse;\n  "])));
  var row = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: row;\n    border: none;\n  "])));
  var body = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: body;\n    text-align: left;\n    max-height: 0;\n    overflow-y: hidden;\n    transition: max-height ", " ", ";\n\n    ", ";\n    p {\n      margin: 0;\n    }\n\n    [dir='rtl'] & {\n      text-align: right;\n    }\n  "])), sysMotionDurationMoveSmall, ppuiEaseMoveSmall, textBody({
    color: sysColorBackgroundHighContrast
  }));
  var bodyOpen = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: body_open;\n    max-height: 125rem;\n  "])));
  var innerBody = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    padding: 0 ", " ", ";\n    padding-top: ", ";\n  "])), sysSpaceLg, sysSpaceLg, sysSpaceXs);

  var getHeaderFont = function (size) {
    if (size === 'small') return css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n        ", "\n      "])), title({
      color: sysColorBackgroundHighContrast
    }));
    return css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n      ", "\n    "])), headingSm({
      color: sysColorBackgroundHighContrast
    }));
  };

  var header = function (size) {
    return css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: header;\n    cursor: pointer;\n    word-break: break-word;\n    padding: ", " ", " ", " ", ";\n    position: relative;\n    background: none;\n    border: none;\n    width: 100%;\n    display: flex;\n    align-items: center;\n    text-align: start;\n    ", ";\n\n    &:focus {\n      ", "\n    }\n\n    &[aria-expanded='true'] {\n      &:before {\n        transform: rotateX(180deg);\n      }\n    }\n\n    [dir='rtl'] & {\n      padding: ", " ", " ", " ", ";\n      text-align: right;\n    }\n  "])), sysSpaceLg, headerPaddingRight, sysSpaceLg, sysSpaceLg, getHeaderFont(size), focusStyles.focusInlineElement, sysSpaceLg, sysSpaceLg, sysSpaceLg, headerPaddingRight);
  };

  var svgIcon = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: svg_icon;\n    color: ", ";\n    position: absolute;\n    right: ", ";\n    transition: transform ", " ", ";\n    [dir='rtl'] & {\n      left: ", ";\n      right: auto;\n    }\n  "])), sysColorBackgroundMediumContrast, sysSpaceLg, sysMotionDurationMoveSmall, ppuiEaseMoveSmall, sysSpaceLg);
  var iconRotate = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: icon_rotate;\n    transform: rotate(180deg);\n  "])));
  return {
    base: base,
    row: row,
    body: body,
    innerBody: innerBody,
    header: header,
    svgIcon: svgIcon,
    iconRotate: iconRotate,
    bodyOpen: bodyOpen
  };
}

var _excluded = ["name", "className", "children", "headerSize"];

var Accordion = function (_ref) {
  var _ref$name = _ref.name,
      name = _ref$name === void 0 ? 'Accordion' : _ref$name,
      className = _ref.className,
      children = _ref.children,
      headerSize = _ref.headerSize,
      rest = _objectWithoutProperties(_ref, _excluded);

  var accordionRows = React.Children.map(children, function (child, index) {
    return React.cloneElement(child, {
      accordionName: name,
      rowNumber: index + 1,
      headerSize: headerSize
    });
  });
  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  return jsx("div", _extends({
    css: _styles.base,
    className: className,
    "data-ppui-info": "accordion_3.7.7"
  }, rest), accordionRows);
};

var _excluded$1 = ["accordionName", "heading", "rowNumber", "className", "headerClassName", "contentClassName", "iconClassName", "isOpen", "onClick", "children", "headerSize"];

var AccordionRow = function (_ref) {
  var accordionName = _ref.accordionName,
      _ref$heading = _ref.heading,
      heading = _ref$heading === void 0 ? 'Heading' : _ref$heading,
      rowNumber = _ref.rowNumber,
      className = _ref.className,
      headerClassName = _ref.headerClassName,
      contentClassName = _ref.contentClassName,
      iconClassName = _ref.iconClassName,
      _ref$isOpen = _ref.isOpen,
      isOpen = _ref$isOpen === void 0 ? false : _ref$isOpen,
      onClick = _ref.onClick,
      children = _ref.children,
      _ref$headerSize = _ref.headerSize,
      headerSize = _ref$headerSize === void 0 ? 'large' : _ref$headerSize,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  var _useState = useState(isOpen),
      _useState2 = _slicedToArray(_useState, 2),
      show = _useState2[0],
      setShowState = _useState2[1];

  var handleClick = function (event) {
    if (onClick) {
      onClick(event, parseInt(rowNumber, 10), !show);
    }

    setShowState(!show);
  };

  useEffect(function () {
    setShowState(isOpen);
  }, [isOpen]); // If theme is not directly provided, get from themeContext

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var stylesBody = [_styles.body, show && _styles.bodyOpen];
  var stylesSvgIcon = [_styles.svgIcon, show && _styles.iconRotate];
  var rowId = "".concat(accordionName, "-row").concat(rowNumber);
  var contentId = "".concat(accordionName, "-content").concat(rowNumber);
  return jsx("div", {
    css: _styles.row,
    className: className,
    "data-ppui": "true"
  }, jsx("button", _extends({
    type: "button",
    id: rowId,
    css: _styles.header(headerSize),
    className: headerClassName,
    "data-row": rowNumber,
    "aria-expanded": show,
    "aria-controls": contentId,
    onClick: function (e) {
      return handleClick(e);
    }
  }, rest, {
    "data-ppui": "true"
  }), heading, jsx(ChevronDownIcon, {
    size: "sm",
    css: stylesSvgIcon,
    className: iconClassName,
    "aria-hidden": "true",
    "data-ppui": "true"
  })), jsx("div", {
    id: contentId,
    css: stylesBody,
    "aria-labelledby": rowId,
    role: "region",
    className: contentClassName,
    "aria-hidden": !show,
    "data-ppui": "true"
  }, jsx("div", {
    css: _styles.innerBody,
    "data-ppui": "true"
  }, children)));
};

export { Accordion, AccordionRow, styles as accordionStyles };
